// data-fit LLC · DecisionFlow · ADMIN Stage
// RampScheduleConfig.jsx · DDL: R20 · API: v1.27 · S247 Phase 10
// Auth: useAuth() direct — ARCH-2 compliant
// Patched: Session 91 — PATCH-02 (ramp_month > 0 enforced client-side per DDL CHECK constraint)
// Patched: S205 — CSS fix
// M040 C2: hardcoded colors → tokens, text-* → text-tok-*
// S247 Phase 10:
//   - OI-AMENDMENT-UI-BEGIN-MODAL: AmendmentBeginModal fires before createRampSchedule and saveEditingRow
//   - OI-AMENDMENT-UI-COMMIT-ON-SAVE: amendment committed after successful create/update
//   - governed_entity_type = "ramp_schedule"
//   - Props added: fiscalPeriodId, wizardSessionId
//
// S470 OI-S469-RAMP-WEBAPP-BULK:
//   CSV bulk upload UI block added. Wires to existing webapp endpoints:
//     POST /api/v1/ramp-schedules/import/validate → session_id + preview
//     POST /api/v1/ramp-schedules/import/confirm  → atomic commit
//   Two-step NN-VALIDATE flow: validate → review → confirm.
//   CSV columns: role_code, ramp_month, ramp_factor, effective_date (4 columns).
//   Dedup key: (tenant_id, role_id, ramp_month, effective_date) — ON CONFLICT UPDATE.
//   No column_mapping layer — ramp schedule import has its own dedicated endpoints
//   (ramp_schedule_import_router.py) not routed through /import/upload.
//
// OPEN ITEMS IN THIS FILE:
// OI-SETUP-RAMP-DELETE-CONFIRM

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import { API_BASE } from './DataSetupFlowShell';
import AmendmentBeginModal from '../admin/AmendmentBeginModal.jsx';

const GOVERNED_ENTITY_TYPE = 'ramp_schedule';

const EMPTY_DRAFT_ROW = { role_id: '', fiscal_period_id: '', ramp_month: '', ramp_factor: '' };

// S470: required columns for client-side CSV header validation.
// Matches _REQUIRED_COLUMNS in ramp_schedule_import_router.py exactly.
const RAMP_REQUIRED_COLS = ['role_code', 'ramp_month', 'ramp_factor', 'effective_date'];

function toIntegerOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getErrorMessage(error, fallback) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function formatRampFactor(value) {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);
  return `${(numericValue * 100).toFixed(2)}%`;
}

async function commitAmendment(amendmentId) {
  if (!amendmentId) return;
  try {
    const res = await fetch(`${API_BASE}/amendments/${amendmentId}/commit`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      console.warn('[Amendment] Commit failed:', body?.error?.message ?? res.status);
    }
  } catch (e) {
    console.warn('[Amendment] Commit network error:', e.message);
  }
}

function RampScheduleConfig({ onBack, onNext, fiscalPeriodId, wizardSessionId }) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.ramp')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  const [roles, setRoles]                         = useState([]);
  const [fiscalPeriods, setFiscalPeriods]         = useState([]);
  const [rows, setRows]                           = useState([]);
  const [draftRow, setDraftRow]                   = useState(EMPTY_DRAFT_ROW);
  const [editingRows, setEditingRows]             = useState({});
  const [loading, setLoading]                     = useState(false);
  const [referenceLoading, setReferenceLoading]   = useState(false);
  const [saving, setSaving]                       = useState(false);
  const [inlineError, setInlineError]             = useState('');
  const [blockingError, setBlockingError]         = useState('');
  const [monotonicityWarnings, setMonotonicityWarnings] = useState([]);
  const [toast, setToast]                         = useState(null);

  // Amendment state
  const [amendModalOpen, setAmendModalOpen] = useState(false);
  const [amendmentId, setAmendmentId]       = useState(null);
  const [pendingAction, setPendingAction]   = useState(null); // 'create' | { type: 'edit', rowId }

  // ── CSV bulk-import state (S470 · NN-VALIDATE: validate → review → confirm) ─
  const [csvFile, setCsvFile]                   = useState(null);
  const [csvSession, setCsvSession]             = useState(null);  // { session_id, rows_valid, rows_rejected, preview, expires_at }
  const [csvCommitted, setCsvCommitted]         = useState(false);
  const [csvImporting, setCsvImporting]         = useState(false);
  const [csvError, setCsvError]                 = useState('');

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options, credentials: 'include',
      headers: { ...(options.headers || {}) }
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Request failed with status ${response.status}`);
    }
    if (response.status === 204) return null;
    return response.json();
  }, []);

  const loadRoles = useCallback(async () => {
    if (!user) return;
    setReferenceLoading(true); setBlockingError('');
    try {
      const [rolesData, fpData] = await Promise.all([
        fetchJson(`${API_BASE}/roles`),
        fetchJson(`${API_BASE}/fiscal-periods`),
      ]);
      setRoles(Array.isArray(rolesData) ? rolesData : rolesData?.roles || rolesData?.data || []);
      setFiscalPeriods(Array.isArray(fpData) ? fpData : fpData?.fiscal_periods || fpData?.data || []);
    } catch (error) {
      setBlockingError(getErrorMessage(error, 'Unable to fetch required reference data.'));
    } finally {
      setReferenceLoading(false);
    }
  }, [fetchJson, user]);

  const loadRampSchedules = useCallback(async () => {
    if (!user) return;
    setLoading(true); setInlineError('');
    try {
      const data = await fetchJson(`${API_BASE}/ramp-schedules`);
      setRows(Array.isArray(data) ? data : data?.ramp_schedules || data?.rows || data?.data || []);
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to fetch ramp schedules.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, user]);

  useEffect(() => { loadRoles(); }, [loadRoles]);
  useEffect(() => { loadRampSchedules(); }, [loadRampSchedules]);

  const roleNameById = useMemo(() => roles.reduce((acc, role) => {
    acc[String(role.role_id)] = role.role_name || role.name; return acc;
  }, {}), [roles]);

  const groupedRowsByRole = useMemo(() => rows.reduce((acc, row) => {
    const key = String(row.role_id);
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {}), [rows]);

  const computeMonotonicityWarnings = useCallback((sourceRows) => {
    const grouped = sourceRows.reduce((acc, row) => {
      const key = String(row.role_id);
      if (!acc[key]) acc[key] = [];
      acc[key].push(row);
      return acc;
    }, {});
    const warnings = [];
    Object.entries(grouped).forEach(([roleId, roleRows]) => {
      const sorted = [...roleRows].sort((l, r) => Number(l.ramp_month) - Number(r.ramp_month));
      for (let i = 1; i < sorted.length; i++) {
        if (Number(sorted[i].ramp_factor) < Number(sorted[i - 1].ramp_factor)) {
          warnings.push({
            role_id: roleId,
            message: `${roleNameById[roleId] || `Role ${roleId}`} has a decreasing ramp factor from month ${sorted[i-1].ramp_month} to month ${sorted[i].ramp_month}.`
          });
        }
      }
    });
    return warnings;
  }, [roleNameById]);

  useEffect(() => { setMonotonicityWarnings(computeMonotonicityWarnings(rows)); }, [computeMonotonicityWarnings, rows]);

  const updateDraftRow = useCallback((field, value) => {
    setDraftRow((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateEditingRow = useCallback((rowId, field, value) => {
    setEditingRows((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], [field]: value }
    }));
  }, []);

  const startEdit = useCallback((row) => {
    const rowId = row.ramp_schedule_id;
    // Replace entire editingRows map — only one row editable at a time.
    // API stores effective_date (date string), not fiscal_period_id — use it directly.
    setEditingRows({
      [rowId]: {
        role_id:          row.role_id != null ? String(row.role_id) : '',
        fiscal_period_id: '', // used to drive period selector in edit form
        effective_date:   row.effective_date ?? '',
        ramp_month:       row.ramp_month != null ? String(row.ramp_month) : '',
        ramp_factor:      row.ramp_factor != null ? String(row.ramp_factor) : '',
      }
    });
    setInlineError('');
  }, []);

  const cancelEdit = useCallback((rowId) => {
    setEditingRows((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
  }, []);

  const validateRow = useCallback((rowValues) => {
    if (!rowValues.role_id) return 'Role is required.';
    if (!rowValues.fiscal_period_id) return 'Fiscal period is required.';
    const rampMonth = toIntegerOrNull(rowValues.ramp_month);
    if (rampMonth === null || rampMonth <= 0) return 'Ramp month must be a positive integer (month 1 or higher).';
    const rampFactor = toNumberOrNull(rowValues.ramp_factor);
    if (rampFactor === null || rampFactor < 0) return 'Ramp factor must be a non-negative number.';
    return '';
  }, []);

  // API contract: POST /ramp-schedules expects
  // { role_id, effective_date, rows: [{ ramp_month, ramp_factor }], amendment_id? }
  // effective_date is resolved from the selected fiscal period's period_start_date —
  // same pattern as PlanBuilder. The user selects the period; we derive the date.
  const buildPayload = useCallback((rowValues, amdId) => {
    const fp = fiscalPeriods.find(
      (p) => String(p.fiscal_period_id) === String(rowValues.fiscal_period_id)
    );
    // If user selected a period, derive effective_date from period_start_date.
    // Otherwise fall back to the existing effective_date on the row (edit case).
    const effectiveDate = fp?.period_start_date ?? fp?.start_date ?? rowValues.effective_date ?? null;
    const payload = {
      role_id:        toIntegerOrNull(rowValues.role_id),
      effective_date: effectiveDate,
      rows: [
        {
          ramp_month:  toIntegerOrNull(rowValues.ramp_month),
          ramp_factor: toNumberOrNull(rowValues.ramp_factor),
        }
      ],
    };
    if (amdId) payload.amendment_id = amdId;
    return payload;
  }, [fiscalPeriods]);

  // ---------------------------------------------------------------------------
  // Amendment-aware create
  // ---------------------------------------------------------------------------
  // During onboarding fiscalPeriodId is null (period open, not locked).
  // Amendment gate must not fire — bypass modal and save directly.
  const handleCreateClick = (event) => {
    event.preventDefault();
    const validationMessage = validateRow(draftRow);
    if (validationMessage) { setInlineError(validationMessage); return; }
    if (amendmentId) {
      runCreate(amendmentId);
    } else if (!fiscalPeriodId) {
      runCreate(null);
    } else {
      setPendingAction('create');
      setAmendModalOpen(true);
    }
  };

  const runCreate = async (amdId) => {
    setSaving(true); setInlineError('');
    try {
      const created = await fetchJson(`${API_BASE}/ramp-schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload(draftRow, amdId)),
      });
      // API returns { rows: [...], rows_written: N } — unpack the rows array.
      const newRows = created?.rows || (Array.isArray(created) ? created : null);
      if (newRows && newRows.length > 0) {
        setRows((prev) => {
          const existingIds = new Set(prev.map((r) => r.ramp_schedule_id));
          return [...newRows.filter((r) => !existingIds.has(r.ramp_schedule_id)), ...prev];
        });
      } else {
        await loadRampSchedules();
      }
      // Commit-on-Save
      if (amdId) { await commitAmendment(amdId); setAmendmentId(null); }
      setDraftRow(EMPTY_DRAFT_ROW);
      showToast('Ramp schedule row created.');
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to create ramp schedule.'));
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Amendment-aware edit save
  // ---------------------------------------------------------------------------
  // Same onboarding bypass as handleCreateClick.
  const handleSaveEditClick = (rowId) => {
    const rowValues = editingRows[rowId];
    if (!rowValues) return;
    const validationMessage = validateRow(rowValues);
    if (validationMessage) { setInlineError(validationMessage); return; }
    if (amendmentId) {
      runSaveEdit(rowId, rowValues, amendmentId);
    } else if (!fiscalPeriodId) {
      runSaveEdit(rowId, rowValues, null);
    } else {
      setPendingAction({ type: 'edit', rowId });
      setAmendModalOpen(true);
    }
  };

  const runSaveEdit = async (rowId, rowValues, amdId) => {
    setSaving(true); setInlineError('');
    try {
      const payload = buildPayload(rowValues, amdId);
      const updated = await fetchJson(`${API_BASE}/ramp-schedules/${rowId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setRows((prev) => prev.map((row) =>
        row.ramp_schedule_id === rowId ? { ...row, ...(updated || payload) } : row
      ));
      setEditingRows((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
      // Commit-on-Save
      if (amdId) { await commitAmendment(amdId); setAmendmentId(null); }
      showToast('Ramp schedule row updated.');
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to update ramp schedule.'));
    } finally {
      setSaving(false);
    }
  };

  const deleteRampSchedule = useCallback(async (rowId) => {
    if (!user) { setInlineError('Authentication is required.'); return; }
    setSaving(true); setInlineError('');
    try {
      // OI-SETUP-RAMP-DELETE-CONFIRM: confirm DELETE /ramp-schedules/{id} exists
      await fetchJson(`${API_BASE}/ramp-schedules/${rowId}`, { method: 'DELETE' });
      setRows((prev) => prev.filter((row) => row.ramp_schedule_id !== rowId));
      setEditingRows((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
      showToast('Ramp schedule row deleted.');
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to delete ramp schedule. It may already be used by a calculation run.'));
    } finally {
      setSaving(false);
    }
  }, [fetchJson, showToast, user]);

  // Amendment modal handlers
  const handleAmendmentBegin = (newAmendmentId) => {
    setAmendmentId(newAmendmentId);
    setAmendModalOpen(false);
    if (pendingAction === 'create') {
      runCreate(newAmendmentId);
    } else if (pendingAction?.type === 'edit') {
      const rowValues = editingRows[pendingAction.rowId];
      if (rowValues) runSaveEdit(pendingAction.rowId, rowValues, newAmendmentId);
    }
    setPendingAction(null);
  };

  // ---------------------------------------------------------------------------
  // S470 — CSV bulk-import handlers
  // Two-step flow: validate (POST /ramp-schedules/import/validate) →
  //                confirm  (POST /ramp-schedules/import/confirm?session_id=...)
  //
  // Step 1 — runCsvValidate: client-side header check, then POST multipart to
  //   /validate. On success returns { session_id, rows_valid, rows_rejected,
  //   preview, expires_at }. On any rejected rows returns 422 — show errors.
  //
  // Step 2 — runCsvConfirm: POST to /confirm?session_id={id}. Atomic upsert.
  //   ON CONFLICT DO UPDATE ramp_factor — safe to re-import.
  //   After success: reload ramp schedules table.
  // ---------------------------------------------------------------------------

  const runCsvValidate = useCallback(async (file) => {
    if (!file) return;
    setCsvImporting(true);
    setCsvError('');
    setCsvSession(null);
    setCsvCommitted(false);
    try {
      // Client-side header check before hitting the network
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const missing = RAMP_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) {
        setCsvError(`Missing required columns: ${missing.join(', ')}`);
        return;
      }

      // POST to /ramp-schedules/import/validate
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/ramp-schedules/import/validate`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const json = await res.json();

      if (!res.ok) {
        // 422 from server: row-level errors in json.detail.error.detail.errors
        const serverErrors = json?.detail?.error?.detail?.errors;
        if (Array.isArray(serverErrors) && serverErrors.length > 0) {
          setCsvError(serverErrors.join('\n'));
        } else {
          const msg = json?.detail?.error?.message || json?.message || `Validation failed (${res.status})`;
          setCsvError(msg);
        }
        return;
      }

      // Success: store session for confirm step
      setCsvSession(json);
    } catch (e) {
      setCsvError(e.message || 'Validation request failed.');
    } finally {
      setCsvImporting(false);
    }
  }, []);

  const runCsvConfirm = useCallback(async () => {
    if (!csvSession?.session_id) return;
    setCsvImporting(true);
    setCsvError('');
    try {
      const res = await fetch(
        `${API_BASE}/ramp-schedules/import/confirm?session_id=${encodeURIComponent(csvSession.session_id)}`,
        { method: 'POST', credentials: 'include' },
      );
      const json = await res.json();
      if (!res.ok) {
        const msg = json?.detail?.error?.message || json?.message || `Confirm failed (${res.status})`;
        setCsvError(msg);
        return;
      }
      setCsvCommitted(true);
      setCsvSession(null);
      setCsvFile(null);
      showToast(`${json.rows_written ?? csvSession.rows_valid} ramp schedule row(s) loaded.`);
      await loadRampSchedules();
    } catch (e) {
      setCsvError(e.message || 'Confirm request failed.');
    } finally {
      setCsvImporting(false);
    }
  }, [csvSession, loadRampSchedules, showToast]);

  const sortedRows = useMemo(() => [...rows].sort((l, r) => {
    const rc = String(l.role_id).localeCompare(String(r.role_id));
    return rc !== 0 ? rc : Number(l.ramp_month) - Number(r.ramp_month);
  }), [rows]);

  const inputClass = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none';
  const tdClass = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
  const btnPrimary   = 'px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
  const btnSecondary = 'px-4 py-2 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';

  if (!user) {
    return (
      <section className="p-6">
        <h2 className="text-tok-xl font-semibold mb-4">Ramp Schedule Config</h2>
        <div role="alert" className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] p-4">
          Authentication is required to configure ramp schedules.
        </div>
      </section>
    );
  }

  if (blockingError) {
    return (
      <section className="p-6">
        <h2 className="text-tok-xl font-semibold mb-4">Ramp Schedule Config</h2>
        <div role="alert" className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] p-4 mb-4">{blockingError}</div>
        <button type="button" onClick={loadRoles} className={btnPrimary}>Retry</button>
      </section>
    );
  }

  return (
    <section className="space-y-6">

      {/* Amendment Begin Modal */}
      <AmendmentBeginModal
        open={amendModalOpen}
        entityType={GOVERNED_ENTITY_TYPE}
        fiscalPeriodId={fiscalPeriodId ? Number(fiscalPeriodId) : null}
        wizardSessionId={wizardSessionId ?? null}
        onBegin={handleAmendmentBegin}
        onCancel={() => { setAmendModalOpen(false); setPendingAction(null); }}
      />

      <header className="flex items-start justify-between">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">SETUP-03</p>
          <h2 className="text-tok-2xl font-bold text-[rgb(var(--color-text))]">Ramp Schedule Config</h2>
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-1">
            Configure ramp factors by role and ramp month. Decreasing ramp factors are warnings, not hard blockers.
          </p>
        </div>
      </header>

      {toast && (
        <div role="status" className="rounded-md bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 text-[rgb(var(--color-success))] px-4 py-3 text-tok-sm">{toast}</div>
      )}
      {inlineError && (
        <div role="alert" className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-4 py-3 text-tok-sm">{inlineError}</div>
      )}
      {amendmentId && (
        <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))] flex items-center justify-between">
          <span>⚠ Amendment open — changes to ramp schedules are governed. Save to commit.</span>
          <button type="button" onClick={async () => {
            try { await fetch(`${API_BASE}/amendments/${amendmentId}/cancel`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } }); } catch (_) {}
            setAmendmentId(null);
          }} className="ml-3 text-tok-xs underline hover:no-underline">Cancel amendment</button>
        </div>
      )}
      {monotonicityWarnings.length > 0 && (
        <section className="bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 rounded-lg p-4" aria-label="Ramp monotonicity warnings">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-warning))] mb-2">Monotonicity warnings</h3>
          <ul className="list-disc list-inside space-y-1">
            {monotonicityWarnings.map((w) => (
              <li key={`${w.role_id}-${w.message}`} className="text-tok-sm text-[rgb(var(--color-warning))]">{w.message}</li>
            ))}
          </ul>
        </section>
      )}

      {/* ── CSV Bulk Import (S470 · OI-S469-RAMP-WEBAPP-BULK) ───────────── */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-5 shadow-sm">
        <div>
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">CSV Bulk Import</h3>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Upload <strong>RampSchedules.csv</strong> to load an entire ramp policy in one pass.
            Validates before committing — existing rows for the same role, month, and effective date are updated in place.
          </p>
        </div>

        {csvError ? (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))] whitespace-pre-wrap">
            {csvError}
          </div>
        ) : null}

        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgb(var(--color-primary))] text-tok-xs font-bold text-white">1</span>
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              RampSchedules.csv — ramp schedule rows
              {csvCommitted ? <span className="ml-2 text-[rgb(var(--color-success))] font-normal">✓ Loaded</span> : null}
            </span>
          </div>

          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Required columns: <code>role_code, ramp_month, ramp_factor, effective_date</code>
            <span className="ml-2 text-[rgb(var(--color-text-muted))]">· ramp_factor 0.0000–1.0000 · effective_date YYYY-MM-DD</span>
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".csv"
              className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setCsvFile(f);
                setCsvSession(null);
                setCsvCommitted(false);
                setCsvError('');
              }}
            />
            <button
              type="button"
              className={btnSecondary}
              disabled={!csvFile || csvImporting}
              onClick={() => runCsvValidate(csvFile)}
            >
              {csvImporting && !csvSession ? 'Validating…' : 'Validate'}
            </button>
          </div>

          {csvSession ? (
            <div className="mt-1 flex flex-col gap-3">
              {/* Validation summary */}
              <div className="flex items-center gap-4 text-tok-sm">
                <span className="text-[rgb(var(--color-text-muted))]">
                  Rows valid: <strong className="text-[rgb(var(--color-success))]">{csvSession.rows_valid}</strong>
                </span>
                {csvSession.rows_rejected > 0 ? (
                  <span className="text-[rgb(var(--color-danger))]">
                    Rows rejected: <strong>{csvSession.rows_rejected}</strong> — fix CSV and re-upload.
                  </span>
                ) : null}
                <span className="text-[rgb(var(--color-text-muted))] text-tok-xs">
                  Session expires: {new Date(csvSession.expires_at).toLocaleTimeString()}
                </span>
              </div>

              {/* Preview table — first 5 rows */}
              {csvSession.preview && csvSession.preview.length > 0 ? (
                <div>
                  <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] mb-1">Preview (first {csvSession.preview.length} rows)</p>
                  <div className="overflow-x-auto rounded-lg border border-[rgb(var(--color-border))]">
                    <table className="min-w-full text-tok-xs">
                      <thead>
                        <tr className="bg-[rgb(var(--color-surface-muted))]">
                          <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">role_code</th>
                          <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">ramp_month</th>
                          <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">ramp_factor</th>
                          <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">effective_date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvSession.preview.map((row, i) => (
                          <tr key={i} className="border-t border-[rgb(var(--color-border))]">
                            <td className="px-3 py-1.5 font-mono text-[rgb(var(--color-text))]">{row.role_code}</td>
                            <td className="px-3 py-1.5 text-[rgb(var(--color-text))]">{row.ramp_month}</td>
                            <td className="px-3 py-1.5 text-[rgb(var(--color-text))]">{formatRampFactor(row.ramp_factor)}</td>
                            <td className="px-3 py-1.5 text-[rgb(var(--color-text))]">{row.effective_date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {/* Confirm button — only shown when all rows valid */}
              {csvSession.rows_rejected === 0 ? (
                <div className="flex items-center gap-3">
                  <span className="text-tok-sm text-[rgb(var(--color-success))]">
                    ✓ Validation passed — {csvSession.rows_valid} row(s) ready to load.
                  </span>
                  <button
                    type="button"
                    className={btnPrimary}
                    disabled={csvImporting}
                    onClick={runCsvConfirm}
                  >
                    {csvImporting ? 'Loading…' : 'Confirm and load'}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* Add row form */}
      <section className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))] mb-4">Add ramp schedule row</h3>
        <form onSubmit={handleCreateClick}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <label>
              Role
              <select className={inputClass} value={draftRow.role_id} onChange={(e) => updateDraftRow('role_id', e.target.value)} disabled={referenceLoading} required>
                <option value="">Select role</option>
                {roles.map((role) => (
                  <option key={role.role_id} value={role.role_id}>{role.role_name || role.name}</option>
                ))}
              </select>
            </label>
            <label>
              Policy effective from
              <select className={inputClass} value={draftRow.fiscal_period_id} onChange={(e) => updateDraftRow('fiscal_period_id', e.target.value)} disabled={referenceLoading} required>
                <option value="">Select period</option>
                {fiscalPeriods.map((p) => (
                  <option key={p.fiscal_period_id} value={p.fiscal_period_id}>{p.period_label || `Period ${p.fiscal_period_id}`}</option>
                ))}
              </select>
            </label>
            <label>
              Ramp month
              <input type="number" className={inputClass} min="1" step="1" value={draftRow.ramp_month} onChange={(e) => updateDraftRow('ramp_month', e.target.value)} required />
            </label>
            <label>
              Ramp factor
              <input type="number" className={inputClass} min="0" step="0.0001" value={draftRow.ramp_factor} onChange={(e) => updateDraftRow('ramp_factor', e.target.value)} required />
            </label>
          </div>
          <div className="mt-4 flex gap-2">
            <button type="submit" disabled={saving} className={btnPrimary}>
              {saving ? 'Saving…' : 'Add row'}
            </button>
            <button type="button" onClick={() => setDraftRow(EMPTY_DRAFT_ROW)} disabled={saving} className={btnSecondary}>Clear</button>
          </div>
          <p className="mt-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
            <strong>Policy effective from</strong> sets when this ramp schedule version takes effect — it is not the rep's start month.
            Ramp month 1 always means the rep's first month in role, regardless of calendar.
            If your ramp policy changes mid-year, create a new schedule with a later effective period; the engine will apply the version active at the rep's ramp start date.
          </p>
        </form>
      </section>

      {/* Ramp schedules table */}
      <section className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Ramp schedules</h3>
          {loading && <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</span>}
        </div>
        <div className="mb-4">
          <button type="button" onClick={loadRampSchedules} disabled={loading} className={btnPrimary}>
            {loading ? 'Refreshing…' : 'Refresh ramp schedules'}
          </button>
        </div>
        {Object.keys(groupedRowsByRole).length === 0 && !loading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No ramp schedules have been configured.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Role</th>
                  <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Policy effective from</th>
                  <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Ramp month</th>
                  <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Ramp factor</th>
                  <th className="px-3 py-2" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => {
                  const rowId = row.ramp_schedule_id;
                  const editValues = editingRows[rowId];
                  const isEditing = Boolean(editValues);
                  return (
                    <tr key={rowId}>
                      <td className={tdClass}>
                        {isEditing ? (
                          <select className={inputClass} value={editValues.role_id} onChange={(e) => updateEditingRow(rowId, 'role_id', e.target.value)} required>
                            <option value="">Select role</option>
                            {roles.map((role) => (<option key={role.role_id} value={role.role_id}>{role.role_name || role.name}</option>))}
                          </select>
                        ) : roleNameById[String(row.role_id)] || row.role_id}
                      </td>
                      <td className={tdClass}>
                        {isEditing ? (
                          <select className={inputClass} value={editValues.fiscal_period_id} onChange={(e) => updateEditingRow(rowId, 'fiscal_period_id', e.target.value)}>
                            <option value="">{editValues.effective_date || 'Select period'}</option>
                            {fiscalPeriods.map((p) => (
                              <option key={p.fiscal_period_id} value={p.fiscal_period_id}>{p.period_label || `Period ${p.fiscal_period_id}`}</option>
                            ))}
                          </select>
                        ) : (row.effective_date || '—')}
                      </td>
                      <td className={tdClass}>
                        {isEditing ? (
                          <input type="number" className={inputClass} min="1" step="1" value={editValues.ramp_month} onChange={(e) => updateEditingRow(rowId, 'ramp_month', e.target.value)} />
                        ) : row.ramp_month}
                      </td>
                      <td className={tdClass}>
                        {isEditing ? (
                          <input type="number" className={inputClass} min="0" step="0.0001" value={editValues.ramp_factor} onChange={(e) => updateEditingRow(rowId, 'ramp_factor', e.target.value)} />
                        ) : formatRampFactor(row.ramp_factor)}
                      </td>
                      <td className={tdClass}>
                        {isEditing ? (
                          <div className="flex gap-2">
                            <button type="button" onClick={() => handleSaveEditClick(rowId)} disabled={saving} className="px-3 py-1 rounded bg-[rgb(var(--color-primary))] text-white text-tok-xs font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">Save</button>
                            <button type="button" onClick={() => cancelEdit(rowId)} disabled={saving} className="px-3 py-1 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-xs font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50">Cancel</button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button type="button" onClick={() => startEdit(row)} className="px-3 py-1 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-xs font-medium hover:bg-[rgb(var(--color-surface-muted))]">Edit</button>
                            <button type="button" onClick={() => deleteRampSchedule(rowId)} disabled={saving} className="px-3 py-1 rounded border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] text-tok-xs font-medium hover:bg-[rgb(var(--color-danger))]/10 disabled:opacity-50">Delete</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <footer className="flex justify-between pt-4 border-t border-[rgb(var(--color-border))]">
        <button type="button" onClick={onBack} className={btnSecondary}>Back</button>
        <button type="button" onClick={onNext} className={btnPrimary}>Continue to plan builder</button>
      </footer>
    </section>
  );
}

export default RampScheduleConfig;
